'use client';

import {
  Badge,
  Button,
  Icons,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
} from '@commenda/kakashi';
import { AlertCircle } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { FormBuilderHook } from '../../hooks/useFormBuilder';
import { STANDARD_CONTEXT_KEYS } from '../../types';
import { getAllFieldIds } from '../../utils/dsl-traversal';
import { EntryTitleTemplateField } from './EntryTitleTemplateField';

export function FormSettingsEditor({
  formBuilder,
}: {
  formBuilder: FormBuilderHook;
}) {
  const { dsl, updateFormMetadata } = formBuilder;
  const [formTitle, setFormTitle] = useState(dsl?.form?.title);

  useEffect(() => {
    setFormTitle(dsl?.form?.title);
  }, [dsl?.form?.title]);

  const handleTitleBlur = () => {
    updateFormMetadata({ title: formTitle });
  };

  const fields = useMemo(() => (dsl ? getAllFieldIds(dsl) : []), [dsl]);
  const display = dsl?.form?.settings?.display;

  const updateEntryTitleTemplate = (value: string) => {
    updateFormMetadata({
      settings: {
        ...dsl?.form?.settings,
        display: { ...display, entryTitleTemplate: value || undefined },
      },
    });
  };

  return (
    <div className="h-full flex flex-col bg-bg-primary border-l border-border-secondary overflow-hidden">
      <div className="p-3.5 px-4 border-b border-border-secondary">
        <h2 className="txt-medium-medium text-text-primary">Form Settings</h2>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {/* Basic Info */}
        <div className="space-y-4">
          <h3 className="txt-small-medium text-text-secondary">
            Basic Information
          </h3>

          <div className="space-y-2">
            <Label>Form Title</Label>
            <Input
              value={formTitle}
              onChange={(e) => setFormTitle(e.target.value)}
              onBlur={handleTitleBlur}
            />
          </div>

          <div className="space-y-2">
            <Label>Form Type</Label>
            <Select
              value={dsl?.form?.type}
              onValueChange={(value) =>
                updateFormMetadata({
                  type: value as 'multi-step' | 'single-page',
                })
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="multi-step">Multi-Step</SelectItem>
                <SelectItem value="single-page">Single Page</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Entry Display */}
        <div className="space-y-4">
          <h3 className="txt-small-medium text-text-secondary">
            Entry Display
          </h3>
          <p className="txt-xs-regular text-text-tertiary">
            How one filled entry of this form is titled where it is shown as a
            card (e.g. a key person card in another form).
          </p>

          <EntryTitleTemplateField
            label="Entry Title Template"
            value={display?.entryTitleTemplate ?? ''}
            fields={fields}
            onChange={updateEntryTitleTemplate}
          />
        </div>

        {/* Required Context */}
        <div className="space-y-4">
          <h3 className="txt-small-medium text-text-secondary">
            Required Context
          </h3>
          <p className="txt-xs-regular text-text-tertiary">
            Toggle which entity IDs are required when rendering this form.
          </p>
          {(['companyId', 'corporationId', 'directorId'] as const).map(
            (key) => {
              const currentRequired =
                dsl?.form?.contextRequirements?.required ?? [];
              const isChecked = currentRequired.includes(key);
              return (
                <div key={key} className="flex items-center justify-between">
                  <Label>{key}</Label>
                  <Switch
                    checked={isChecked}
                    onCheckedChange={(checked) => {
                      const updated = checked
                        ? [...currentRequired, key]
                        : currentRequired.filter((k) => k !== key);
                      updateFormMetadata({
                        contextRequirements: {
                          ...dsl?.form?.contextRequirements,
                          required: updated,
                        },
                      });
                    }}
                  />
                </div>
              );
            }
          )}
        </div>

        {/* Context Requirements */}
        <div className="space-y-4">
          {/* Expected Context Keys */}
          <div className="space-y-3 pt-3 border-t border-border-secondary">
            <div>
              <p className="txt-small-medium text-text-primary flex items-center gap-2">
                Expected Context Keys
                <Badge variant="blue" size="sm">
                  New
                </Badge>
              </p>
              <p className="txt-xs-regular text-text-tertiary">
                Add expected context keys this form requires (e.g.,
                jurisdictionId, country)
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {(dsl?.form?.contextRequirements?.required || [])
                .filter(
                  (k) =>
                    !(STANDARD_CONTEXT_KEYS as readonly string[]).includes(k)
                )
                .map((key) => (
                  <div
                    key={key}
                    className="flex items-center gap-1 px-2 py-1 bg-bg-secondary rounded-md border border-border-secondary"
                  >
                    <span className="txt-xs-medium text-text-primary">
                      {key}
                    </span>
                    <Icons.X
                      className="w-3 h-3 cursor-pointer text-text-tertiary hover:text-text-primary"
                      onClick={() => {
                        const currentRequired =
                          dsl?.form?.contextRequirements?.required ?? [];
                        updateFormMetadata({
                          contextRequirements: {
                            ...dsl?.form?.contextRequirements,
                            required: currentRequired.filter(
                              (k2) => k2 !== key
                            ),
                          },
                        });
                      }}
                    />
                  </div>
                ))}
            </div>
            <div className="flex gap-2">
              <Input
                id="custom-context-key-input"
                placeholder="Enter context key..."
                className="flex-1"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    const input = e.target as HTMLInputElement;
                    const value = input.value.trim();
                    if (value) {
                      const currentRequired =
                        dsl?.form?.contextRequirements?.required ?? [];
                      if (!currentRequired.includes(value)) {
                        updateFormMetadata({
                          contextRequirements: {
                            ...dsl?.form?.contextRequirements,
                            required: [...currentRequired, value],
                          },
                        });
                      }
                      input.value = '';
                    }
                  }
                }}
              />
              <Button
                variant="secondary"
                onClick={() => {
                  const input = document.getElementById(
                    'custom-context-key-input'
                  ) as HTMLInputElement;
                  const value = input?.value?.trim();
                  if (value) {
                    const currentRequired =
                      dsl?.form?.contextRequirements?.required ?? [];
                    if (!currentRequired.includes(value)) {
                      updateFormMetadata({
                        contextRequirements: {
                          ...dsl?.form?.contextRequirements,
                          required: [...currentRequired, value],
                        },
                      });
                    }
                    input.value = '';
                  }
                }}
              >
                Add
              </Button>
            </div>
          </div>

          <div className="p-3 bg-bg-secondary rounded-md border border-border-secondary">
            <p className="txt-xs-regular text-text-tertiary">
              <AlertCircle className="inline-block w-3 h-3 mr-1" />
              Forms using Commenda OS fields will auto-detect required context
              based on configured data sources.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
